import { IconRefresh } from '@tabler/icons-react';
import React from 'react';
import { UncontrolledTooltip } from 'reactstrap';
import { formatDateShort, formatDecimalAsPercentage, numeralFormatter } from 'ns_libs/formatter';
import NSButton from 'bricks/NSButton/NSButton';
import RenderIf from 'components/RenderIf/RenderIf';
import { useAuthContext } from 'contexts/AuthContext';
import useToast from 'hooks/useToast';
import { FEATURE_IDS } from 'services/constants';
import { AccessLevels } from 'types/auth';
import { useIsMutating } from '@tanstack/react-query';
import assertNever from 'helpers/assertNever';
import {
    IRefreshActualsVariables,
    REFRESH_PRO_FORMA_ACTUALS_MUTATION_KEY,
    useRefreshProFormaActuals,
} from '../hooks/useRefreshProFormaActuals';
import { PROJECT_ACTUALS_TYPE } from '../constants';
import { Deal } from '../types';

interface ProjectActualsCellProps {
    deal: Deal;
    type: PROJECT_ACTUALS_TYPE;
    value?: number;
}

const formatActualsValue = (type: PROJECT_ACTUALS_TYPE, value?: number): string => {
    if (value == null) return '-';

    switch (type) {
        case PROJECT_ACTUALS_TYPE.IRR:
            return formatDecimalAsPercentage(value, 2);
        case PROJECT_ACTUALS_TYPE.EQUITY_MULTIPLE:
            return `${numeralFormatter(value, '0.00')}x`;
        default:
            return assertNever(type);
    }
};

const ProjectActualsCell: React.FC<ProjectActualsCellProps> = ({ deal, type, value }) => {
    const { selectedOrganizationId, isFeatureAccessibleToUser } = useAuthContext();
    const { showSuccess, showError } = useToast();
    const { mutate: refreshActuals } = useRefreshProFormaActuals();

    const { id: dealId, actualsRefreshedAt, activeProFormaId } = deal;

    const isRefreshing = useIsMutating({
        mutationKey: REFRESH_PRO_FORMA_ACTUALS_MUTATION_KEY,
        predicate: mutation => (mutation.state.variables as IRefreshActualsVariables | undefined)?.dealId === dealId,
    }) > 0;

    const formattedValue = formatActualsValue(type, value);
    const hasValue = value != null;
    const tooltipId = `project-actuals-${type}-${dealId}`;

    const hasWriteAccess = isFeatureAccessibleToUser([FEATURE_IDS.PRO_FORMA], AccessLevels.READ_WRITE_ACCESS);
    const canRefreshActuals = hasWriteAccess
        && deal.activeProFormaSubprojectId != null
        && activeProFormaId != null;

    const handleRefresh = () => {
        if (!activeProFormaId || !selectedOrganizationId) return;

        refreshActuals(
            { organizationId: selectedOrganizationId, dealId, proFormaId: activeProFormaId },
            {
                onSuccess: () => showSuccess('Project actuals refreshed.'),
                onError: error => showError(error.message || 'Failed to refresh project actuals.'),
            },
        );
    };

    return (
        <span className="d-inline-flex align-items-center justify-content-end">
            {hasValue && actualsRefreshedAt ? (
                <>
                    <span id={tooltipId}>{formattedValue}</span>
                    <UncontrolledTooltip
                        target={tooltipId}
                        placement="top-end"
                    >
                        {`Last updated: ${formatDateShort(actualsRefreshedAt)}`}
                    </UncontrolledTooltip>
                </>
            ) : (
                <span>{formattedValue}</span>
            )}
            <RenderIf isTrue={canRefreshActuals}>
                <NSButton
                    id={`refresh-actuals-${type}-${dealId}`}
                    icon={IconRefresh}
                    size="sm"
                    className="py-0 ml-1 bg-none hover:text-dark hover:bg-light"
                    callback={handleRefresh}
                    isLoading={isRefreshing}
                    tooltip="Refresh project actuals"
                />
            </RenderIf>
        </span>
    );
};

export default React.memo(ProjectActualsCell);
